/* eslint-disable */
import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useParams, useLocation } from 'react-router-dom';

import LarinzDataProvider from '@/larinz/dataProvider';
import { AuthProvider, useAuth } from '@/larinz/auth';
import SEO, { ROUTE_SEO } from '@/larinz/SEO';
import {
  Nav, MediaLightbox,
  HomePage, WorkPage, AboutPage, ContactPage,
  StudioPage, ProjectPage,
} from '@/larinz/components.jsx';
import { projectSlug, findProjectBySlug } from '@/larinz/slug';
import AdminLogin from '@/admin/AdminLogin';
import AdminDashboard from '@/admin/AdminDashboard';
import '@/App.css';

function useLarinzNav() {
  const navigate = useNavigate();
  const onNav = (page) => {
    const path =
      page === 'home' ? '/' :
      page === 'work' ? '/work' :
      page === 'studio' ? '/studio' :
      page === 'about' ? '/about' :
      page === 'contact' ? '/contact' : '/';
    navigate(path);
  };
  const onOpen = (project) => navigate(`/work/${projectSlug(project)}`);
  return { onNav, onOpen, navigate };
}

function ScrollToTop() {
  const location = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'instant' }); }, [location.pathname]);
  return null;
}

function StickyNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const path = location.pathname;
  if (path === '/' || (path.startsWith('/work/') && path !== '/work') || path.startsWith('/admin')) return null;
  const variant = path === '/contact' ? 'dark' : 'light';
  const current =
    path === '/work' ? 'work' :
    path === '/studio' ? 'studio' :
    path === '/about' ? 'about' :
    path === '/contact' ? 'contact' : 'home';
  return <Nav variant={variant} current={current} onNav={p => navigate(p === 'home' ? '/' : `/${p}`)} />;
}

function LegacyNavBridge() {
  const navigate = useNavigate();
  useEffect(() => {
    const h = (e) => {
      const page = e.detail;
      const path =
        page === 'home' ? '/' :
        page === 'work' ? '/work' :
        page === 'studio' ? '/studio' :
        page === 'about' ? '/about' :
        page === 'contact' ? '/contact' : '/';
      navigate(path);
    };
    window.addEventListener('ll-nav', h);
    return () => window.removeEventListener('ll-nav', h);
  }, [navigate]);
  return null;
}

function HomeRoute()    { const { onNav, onOpen } = useLarinzNav(); return <><SEO {...ROUTE_SEO.home}    path="/" />        <HomePage onNav={onNav} onOpen={onOpen} /></>; }
function WorkRoute()    { const { onNav, onOpen } = useLarinzNav(); return <><SEO {...ROUTE_SEO.work}    path="/work" />    <WorkPage onNav={onNav} onOpen={onOpen} /></>; }
function StudioRoute()  { const { onNav } = useLarinzNav();         return <><SEO {...ROUTE_SEO.studio}  path="/studio" />  <StudioPage onNav={onNav} /></>; }
function AboutRoute()   { const { onNav } = useLarinzNav();         return <><SEO {...ROUTE_SEO.about}   path="/about" />   <AboutPage onNav={onNav} /></>; }
function ContactRoute() { const { onNav } = useLarinzNav();         return <><SEO {...ROUTE_SEO.contact} path="/contact" /> <ContactPage onNav={onNav} /></>; }

function ProjectRoute() {
  const { slug } = useParams();
  const { navigate, onOpen } = useLarinzNav();
  const project = findProjectBySlug(slug);
  if (!project) {
    return (
      <>
        <SEO title="Project not found · Larinz Labz" description="The project you're looking for doesn't exist or has moved." path={`/work/${slug}`} />
        <div style={{ padding: '160px 10%', textAlign: 'center', fontFamily: 'Inter, sans-serif' }}>
          <h1 style={{ fontWeight: 900, fontSize: 48, textTransform: 'uppercase' }}>Project not found</h1>
          <p style={{ marginTop: 16 }}>
            <button onClick={() => navigate('/work')} data-testid="back-to-work-btn" style={{ background: '#A5FF12', color: '#111', border: 'none', padding: '12px 24px', fontWeight: 800, cursor: 'pointer', borderRadius: 999 }}>← Back to Work</button>
          </p>
        </div>
      </>
    );
  }
  const title = `${project.name} · Larinz Labz`;
  const desc = project.tagline || project.story?.slice(0, 160) || ROUTE_SEO.work.description;
  return (
    <>
      <SEO title={title} description={desc} path={`/work/${slug}`} />
      <ProjectPage project={project} onBack={() => navigate('/work')} onOpenProject={onOpen} />
    </>
  );
}

function AppRoutes() {
  return (
    <>
      <ScrollToTop />
      <LegacyNavBridge />
      <StickyNav />
      <Routes>
        <Route path="/" element={<HomeRoute />} />
        <Route path="/work" element={<WorkRoute />} />
        <Route path="/work/:slug" element={<ProjectRoute />} />
        <Route path="/studio" element={<StudioRoute />} />
        <Route path="/about" element={<AboutRoute />} />
        <Route path="/contact" element={<ContactRoute />} />
        <Route path="/admin" element={<AdminLogin />} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/dashboard" element={<AdminDashboard />} />
        <Route path="/admin/*" element={<AdminDashboard />} />
        <Route path="*" element={<HomeRoute />} />
      </Routes>
      <MediaLightbox />
    </>
  );
}

function LoadingScreen() {
  return (
    <div data-testid="loading-screen" style={{
      position: 'fixed', inset: 0, background: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: 'Inter, sans-serif', fontSize: 12, letterSpacing: 3,
      textTransform: 'uppercase', color: '#888', fontWeight: 700,
    }}>
      Larinz Labz · Loading…
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <LarinzDataProvider fallback={<LoadingScreen />}>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </LarinzDataProvider>
    </AuthProvider>
  );
}
